import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert,
    Button,
    Grid,
    Card,
    CardContent,
    Typography,
    Stack,
    Box,
    CircularProgress,
    ToggleButtonGroup,
} from '@mui/joy';
import {
    People,
    DeviceHub,
    MonitorHeart,
    Videocam,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { adminHeaders } from '../../utils/adminHeaders';

const API_BASE = process.env.REACT_APP_ADMIN_API_BASE || 'http://localhost:9300';

// The backend caches the snapshot for 30 s.
const REFRESH_MS = 60 * 1000;

// Below this share of the period the services were up, health shows amber.
const LOW_UPTIME = 0.99;

// Checks that are fine when not UP: no provider secret yet, pushes not held.
const OK_STATES = ['UP', 'OK', 'NOT_CONFIGURED'];

const PERIODS = [
    { key: '24h', label: 'Day', phrase: 'in the last day' },
    { key: '7d', label: 'Week', phrase: 'in the last week' },
    { key: '30d', label: 'Month', phrase: 'in the last month' },
];

const fmt = (n) => (n == null ? '—' : n.toLocaleString());

const compactFormat = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 2 });
// 16.67k, 3.21k, 1.2M
const compact = (n) => (n == null ? '—' : compactFormat.format(n).toLowerCase());

const formatHours = (ms) => {
    const hours = ms / 3600000;
    if (hours < 10) return `${hours.toFixed(1)} hours`;
    return `${compact(Math.round(hours))} hours`;
};

// Shared with the footage shares table.
export const formatBytes = (bytes) => {
    const gb = bytes / 1e9;
    if (gb < 1) return `${(bytes / 1e6).toFixed(1)} MB`;
    if (gb < 1000) return `${gb.toFixed(1)} GB`;
    return `${(gb / 1000).toFixed(2)} TB`;
};

const formatUptime = (share) => {
    if (share == null) return '—';
    const percent = share * 100;
    return percent >= 100 ? '100%' : `${Math.min(percent, 99.99).toFixed(2)}%`;
};

const formatAgo = (ms) => {
    const seconds = Math.max(0, Math.round((Date.now() - ms) / 1000));
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return `${Math.floor(seconds / 3600)}h ago`;
};

const StatCard = ({ title, value, icon: Icon, color = 'primary', loading = false, lines = [] }) => (
    <Card variant="outlined" sx={{ width: '100%', minHeight: '150px', display: 'flex', flexDirection: 'column' }}>
        <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography level="body-sm" textColor="neutral.600" noWrap>
                        {title}
                    </Typography>
                    {loading ? (
                        <CircularProgress size="sm" sx={{ mt: 1 }} />
                    ) : (
                        <Typography level="h2" sx={{ mt: 0.5, lineHeight: 1.2, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                            {value}
                        </Typography>
                    )}
                    {!loading && lines.map((line) => (
                        <Typography key={line} level="body-xs" textColor="neutral.500" sx={{ mt: 0.5, lineHeight: 1.3 }}>
                            {line}
                        </Typography>
                    ))}
                </Box>
                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: '50%',
                        backgroundColor: `${color}.softBg`,
                        color: `${color}.500`,
                        flexShrink: 0,
                        ml: 2,
                    }}
                >
                    <Icon sx={{ fontSize: 24 }} />
                </Box>
            </Stack>
        </CardContent>
    </Card>
);

const unavailable = { value: '—', lines: ['Unavailable'], color: 'neutral' };

const hubsCard = (hubs, period) => {
    if (!hubs) return unavailable;
    const offline = hubs.total - hubs.online;
    const counts = hubs.windows?.[period.key] || {};
    const lines = [
        `${fmt(offline)} offline now`,
        `+${fmt(counts.new)} new · ${fmt(counts.active)} active ${period.phrase}`,
    ];
    if (!hubs.presence_fresh) {
        lines.push('Signaling is not reporting: online state may be stale');
    }
    return {
        value: `${fmt(hubs.online)}/${fmt(hubs.total)}`,
        lines,
        color: !hubs.presence_fresh || offline > 0 ? 'warning' : 'success',
    };
};

const usersCard = (users, period) => {
    if (!users) return unavailable;
    const counts = users.windows?.[period.key] || {};
    return {
        value: fmt(users.signed_in),
        lines: [
            `of ${fmt(users.registered)} registered`,
            `+${fmt(counts.new)} new · ${fmt(counts.active)} active ${period.phrase}`,
        ],
        color: 'primary',
    };
};

const healthCard = (health, period) => {
    if (!health) return unavailable;
    const checks = health.checks || {};
    const names = Object.keys(checks);
    const down = names.filter((name) => !OK_STATES.includes(checks[name])).sort();
    const uptime = `${formatUptime(health.uptime?.[period.key])} uptime ${period.phrase}`;
    if (health.status !== 'UP') {
        return {
            value: 'Degraded',
            lines: [uptime, `Down: ${down.filter((name) => name !== 'hub_status_pushes').join(', ')}`],
            color: 'danger',
        };
    }
    if (checks.hub_status_pushes === 'HELD') {
        return {
            value: 'Healthy',
            lines: [uptime, 'Hub offline pushes held: many hubs dropped at once'],
            color: 'warning',
        };
    }
    if (down.length > 0) {
        return { value: 'Healthy', lines: [uptime, `Check: ${down.join(', ')}`], color: 'warning' };
    }
    const share = health.uptime?.[period.key];
    return {
        value: 'Healthy',
        lines: [uptime, `All ${names.length} checks up now`],
        color: share != null && share < LOW_UPTIME ? 'warning' : 'success',
    };
};

/** How much footage the hubs recorded, for a sense of scale. */
const FootageCard = ({ footage, period, loading }) => {
    const totals = footage?.windows?.[period.key];

    return (
        <Card variant="outlined">
            <CardContent>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                    <Box sx={{ p: 1.5, borderRadius: '50%', backgroundColor: 'primary.softBg', color: 'primary.500', display: 'flex' }}>
                        <Videocam sx={{ fontSize: 24 }} />
                    </Box>
                    <Typography level="body-sm" textColor="neutral.600">
                        Footage Recorded
                    </Typography>
                </Stack>
                {loading ? (
                    <CircularProgress size="sm" />
                ) : !footage || !totals ? (
                    <Typography level="body-sm" textColor="neutral.500">Unavailable</Typography>
                ) : (
                    <>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 4 }} alignItems={{ sm: 'baseline' }}>
                            <Typography level="h2" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                                {formatHours(totals.duration_ms)}
                            </Typography>
                            <Typography level="h2" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                                {formatBytes(totals.size_bytes)}
                            </Typography>
                        </Stack>
                        <Typography level="body-sm" textColor="neutral.600" sx={{ mt: 1 }}>
                            {compact(footage.cameras)} cameras on {compact(footage.hubs)} hubs recorded{' '}
                            {formatHours(totals.duration_ms)} / {formatBytes(totals.size_bytes)} of footage {period.phrase}
                            {' '}({compact(totals.recordings)} recordings).
                        </Typography>
                    </>
                )}
            </CardContent>
        </Card>
    );
};

/** Snapshot of the system over the last day, week or month: hubs, users, service health and footage recorded. */
const DashboardWidgets = () => {
    const { session } = useAuth();
    const [stats, setStats] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [periodKey, setPeriodKey] = useState('24h');
    const period = PERIODS.find((w) => w.key === periodKey);
    const sessionRef = useRef(session);
    sessionRef.current = session;

    const load = useCallback(async () => {
        if (!sessionRef.current?.accessToken) return;
        try {
            const res = await fetch(`${API_BASE}/dashboard/stats`, { headers: adminHeaders(sessionRef.current) });
            const body = await res.json().catch(() => null);
            if (!res.ok) {
                setError(typeof body === 'string' ? body : `Failed to load system overview (${res.status}).`);
                return;
            }
            setError(null);
            setStats(body);
        } catch (e) {
            setError('Unable to reach the admin API.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const timer = setInterval(load, REFRESH_MS);
        return () => clearInterval(timer);
    }, [load, session?.accessToken]);

    const waiting = loading && !stats;
    const cards = [
        { title: 'Hubs Online', icon: DeviceHub, ...hubsCard(stats?.hubs, period) },
        { title: 'Users Signed In', icon: People, ...usersCard(stats?.users, period) },
        { title: 'System Health', icon: MonitorHeart, ...healthCard(stats?.health, period) },
    ];

    return (
        <Box sx={{ mb: 4 }}>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                spacing={1.5}
                sx={{ mb: 3 }}
            >
                <Stack direction="row" alignItems="baseline" spacing={2}>
                    <Typography level="h4">System Overview</Typography>
                    {stats?.generated_at_ms && (
                        <Typography level="body-xs" textColor="neutral.500">
                            Updated {formatAgo(stats.generated_at_ms)}
                        </Typography>
                    )}
                </Stack>
                <ToggleButtonGroup size="sm" value={periodKey} onChange={(event, value) => value && setPeriodKey(value)}>
                    {PERIODS.map((w) => (
                        <Button key={w.key} value={w.key}>
                            {w.label}
                        </Button>
                    ))}
                </ToggleButtonGroup>
            </Stack>
            {error && (
                <Alert color="danger" variant="soft" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            {stats?.errors?.length > 0 && (
                <Alert color="warning" variant="soft" sx={{ mb: 2 }}>
                    Some numbers could not be read: {stats.errors.join('; ')}
                </Alert>
            )}
            <Grid container spacing={4} sx={{ alignItems: 'stretch' }}>
                {cards.map((card) => (
                    <Grid key={card.title} xs={12} md={4} sx={{ display: 'flex' }}>
                        <StatCard {...card} loading={waiting} />
                    </Grid>
                ))}
                <Grid xs={12}>
                    <FootageCard footage={stats?.footage} period={period} loading={waiting} />
                </Grid>
            </Grid>
        </Box>
    );
};

export default DashboardWidgets;
